import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  BIG_MOVE_ACCESS_OPTIONS,
  BIG_MOVE_CARGO_OPTIONS,
  BIG_MOVE_TYPE_OPTIONS,
  BIG_MOVE_VEHICLE_OPTIONS,
  COURIER_HANDLING_FLAGS,
  COURIER_ITEM_OPTIONS,
  COURIER_WEIGHT_LIMIT_KG,
  DELIVERY_TERMS_SUMMARY,
  PAYER_CATEGORY_OPTIONS,
  PAYER_CONDITION_OPTIONS,
  PAYER_FULFILLMENT_OPTIONS,
  PAYER_PAYMENT_METHOD_OPTIONS,
  PAYER_PROOF_OPTIONS,
  PAYER_PURPOSE_OPTIONS,
  PAYER_SUBSTITUTION_OPTIONS,
  DEMO_PROXY_RUNNERS,
  exceedsCourierWeightLimit,
  parseWeightKg,
} from '../../config/deliveryCargoOptions';
import {
  EXCHANGE_MODE_OPTIONS,
  ONLINE_FULFILLMENT_OPTIONS,
  SETTLEMENT_METHOD_OPTIONS,
} from '../../config/exchangeOnlineOptions';
import { ESCROW_MILESTONE_OPTIONS } from '../../util/jobLifecycle';
import { getSimpleAppExperience } from '../../config/simpleAppExperiences';
import { useActivityCart } from '../../context/ActivityCartContext';
import { useJobBoard } from '../../context/JobBoardContext';
import {
  fieldsSatisfied,
  resolveFormSteps,
  shouldOfferProgressive,
} from '../../util/progressiveForm';
import { CommunityAskBar } from '../CommunityChannels/CommunityChannels';
import ColdStartModes from './ColdStartModes';
import JobWorkspace from './JobWorkspace';
import ProgressiveFormChrome from './ProgressiveFormChrome';
import IdentitySyncBar from './IdentitySyncBar';
import SupplyWaitlistForm from './SupplyWaitlistForm';

import css from './SimpleAppExperience.module.css';
import svcCss from './ServicesAppExperience.module.css';

const TABS = {
  payer: 'payer',
  courier: 'courier',
  bigMove: 'bigMove',
};

const STAGES = {
  explore: 'explore',
  detail: 'detail',
  create: 'create',
  review: 'review',
  job: 'job',
  success: 'success',
};

const TAB_LABELS = {
  payer: 'Payer',
  courier: 'Courier',
  bigMove: 'Big Move',
};

const OPTIONS_BY_SOURCE = {
  courierItems: COURIER_ITEM_OPTIONS,
  courierHandling: COURIER_HANDLING_FLAGS,
  bigMoveTypes: BIG_MOVE_TYPE_OPTIONS.map(o => ({ id: o.value, label: o.label, value: o.value })),
  bigMoveCargo: BIG_MOVE_CARGO_OPTIONS,
  bigMoveAccess: BIG_MOVE_ACCESS_OPTIONS,
  bigMoveVehicles: BIG_MOVE_VEHICLE_OPTIONS.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  })),
  payerPurpose: PAYER_PURPOSE_OPTIONS,
  payerCategory: PAYER_CATEGORY_OPTIONS,
  payerCondition: PAYER_CONDITION_OPTIONS.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  })),
  payerPaymentMethod: PAYER_PAYMENT_METHOD_OPTIONS.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  })),
  payerProof: PAYER_PROOF_OPTIONS,
  payerSubstitution: PAYER_SUBSTITUTION_OPTIONS.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  })),
  payerFulfillment: PAYER_FULFILLMENT_OPTIONS.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  })),
  exchangeMode: EXCHANGE_MODE_OPTIONS.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  })),
  settlementMethods: SETTLEMENT_METHOD_OPTIONS,
  onlineFulfillment: ONLINE_FULFILLMENT_OPTIONS,
  escrowMilestone: ESCROW_MILESTONE_OPTIONS.map(o => ({
    id: o.value,
    label: o.label,
    value: o.value,
  })),
};

const emptyForm = () => ({
  enabledAddons: {},
  addonNotes: {},
  multiselect: {},
  termsAccepted: false,
});

const resolveFieldOptions = field => {
  if (field.optionsSource && OPTIONS_BY_SOURCE[field.optionsSource]) {
    return OPTIONS_BY_SOURCE[field.optionsSource];
  }
  return (field.options || []).map(o =>
    typeof o === 'string' ? { id: o, label: o, value: o } : { id: o.value, label: o.label, value: o.value }
  );
};

/**
 * Payers & Delivery hub — Payer | Courier | Big Move.
 */
const PayersDeliveryAppExperience = ({ app }) => {
  const experience = getSimpleAppExperience(app.id);
  const { upsertDraft } = useActivityCart();
  const { createJob, listJobs, statusLabel, syncStatus } = useJobBoard();

  const [tab, setTab] = useState(TABS.payer);
  const [stage, setStage] = useState(STAGES.explore);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formStepIndex, setFormStepIndex] = useState(0);
  const [onePager, setOnePager] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);

  const tabCopy = experience?.tabs?.[tab] || {};
  const fields = tabCopy.formFields || experience?.formFields || [];
  const addons = tabCopy.addons || [];
  const demoItems = tabCopy.demoItems || [];
  const enabledAddons = formData.enabledAddons || {};
  const addonNotes = formData.addonNotes || {};
  const multiselect = formData.multiselect || {};

  const formSteps = useMemo(
    () => resolveFormSteps(fields, tabCopy.formSteps || experience?.formSteps),
    [fields, tabCopy.formSteps, experience?.formSteps]
  );
  const progressive = shouldOfferProgressive(formSteps);
  const showAddonsOnStep =
    !progressive || onePager || formStepIndex >= formSteps.length - 1;
  const visibleFields =
    !progressive || onePager ? fields : formSteps[formStepIndex]?.fields || [];
  const isLastFormStep = !progressive || onePager || formStepIndex >= formSteps.length - 1;

  const weightKg = parseWeightKg(formData.weightKg);
  const showBigMoveNudge =
    tab === TABS.courier && exceedsCourierWeightLimit(weightKg);

  const canAdvanceStep = useMemo(
    () => fieldsSatisfied(visibleFields, formData, multiselect),
    [visibleFields, formData, multiselect]
  );

  const canSubmit = useMemo(
    () => fieldsSatisfied(fields, formData, multiselect),
    [fields, formData, multiselect]
  );

  const canConfirm = canSubmit && Boolean(formData.termsAccepted);

  if (!experience) {
    return null;
  }

  const switchTab = next => {
    setTab(next);
    setStage(STAGES.explore);
    setSelected(null);
    setFormData(emptyForm());
    setFormStepIndex(0);
    setOnePager(false);
  };

  const persist = (status, title, data) => {
    upsertDraft({
      appId: app.id,
      title,
      stepIndex: 0,
      formData: { tab, ...data },
      status,
      resumePath: `${app.hubPath}?from=landing`,
      launchHref: app.launchHref,
    });
  };

  const finish = (title, data) => {
    persist('completed', title, data);
    setStage(STAGES.success);
  };

  const resetExplore = () => {
    setSelected(null);
    setFormData(emptyForm());
    setFormStepIndex(0);
    setOnePager(false);
    setActiveJobId(null);
    setStage(STAGES.explore);
  };

  const myJobs = listJobs({ appId: app.id, kind: 'proxy' });

  const publishProxyJob = () => {
    if (!canConfirm && !selected) {
      return;
    }
    const title =
      selected?.title ||
      formData.itemTitle ||
      formData.purchaseDesc?.slice?.(0, 48) ||
      formData.pickup ||
      formData.from ||
      `${TAB_LABELS[tab]} request`;
    const job = createJob({
      appId: app.id,
      kind: 'proxy',
      title,
      brief: selected
        ? {
            tab,
            demoItemId: selected.id,
            demoTitle: selected.title,
            useEscrow: formData.useEscrow || 'yes',
            escrowMilestone: formData.escrowMilestone || 'deposit',
            escrowAmount: formData.escrowAmount || formData.cashBudget,
            currency: formData.currency || 'JMD',
            budget: formData.cashBudget,
          }
        : { tab, ...formData, budget: formData.cashBudget || formData.escrowAmount },
      scheduleWindow: formData.timeWindow || formData.moveDate || formData.deadline || '',
      demoProviders: DEMO_PROXY_RUNNERS,
      autoQuotes: true,
      matchMode: formData.matchMode || 'best_quote',
    });
    persist('in_progress', job.title, { ...formData, jobId: job.id, tab });
    setActiveJobId(job.id);
    setStage(STAGES.job);
  };

  const fieldLabel = id => fields.find(f => f.id === id)?.label || id;

  const optionLabel = (field, value) => {
    const opts = resolveFieldOptions(field);
    return opts.find(o => (o.value || o.id) === value)?.label || value;
  };

  const toggleAddon = addonId => {
    setFormData(prev => {
      const nextEnabled = { ...(prev.enabledAddons || {}) };
      const nextNotes = { ...(prev.addonNotes || {}) };
      if (nextEnabled[addonId]) {
        delete nextEnabled[addonId];
        delete nextNotes[addonId];
      } else {
        nextEnabled[addonId] = true;
      }
      return { ...prev, enabledAddons: nextEnabled, addonNotes: nextNotes };
    });
  };

  const setAddonNote = (addonId, value) => {
    setFormData(prev => ({
      ...prev,
      addonNotes: { ...(prev.addonNotes || {}), [addonId]: value },
    }));
  };

  const toggleMulti = (fieldId, optionId) => {
    setFormData(prev => {
      const current = new Set(prev.multiselect?.[fieldId] || []);
      if (current.has(optionId)) {
        current.delete(optionId);
      } else {
        current.add(optionId);
      }
      return {
        ...prev,
        multiselect: { ...(prev.multiselect || {}), [fieldId]: [...current] },
      };
    });
  };

  const startCreate = () => {
    setSelected(null);
    setFormData(emptyForm());
    setFormStepIndex(0);
    setOnePager(false);
    setStage(STAGES.create);
  };

  const backFromCreate = () => {
    if (progressive && !onePager && formStepIndex > 0) {
      setFormStepIndex(i => i - 1);
      return;
    }
    resetExplore();
  };

  const advanceCreate = () => {
    if (!canAdvanceStep || (isLastFormStep && showBigMoveNudge)) {
      return;
    }
    if (!isLastFormStep) {
      setFormStepIndex(i => i + 1);
      return;
    }
    if (!canSubmit) {
      return;
    }
    setStage(STAGES.review);
  };

  const goToBigMove = () => {
    setTab(TABS.bigMove);
    setFormStepIndex(0);
    setOnePager(false);
    setStage(STAGES.create);
    setSelected(null);
    setFormData(prev => ({
      ...emptyForm(),
      weightKg: prev.weightKg,
      packageDesc: prev.packageDesc,
      pickup: prev.pickup,
      dropoff: prev.dropoff,
      from: prev.pickup,
      to: prev.dropoff,
      cargoDesc: prev.packageDesc,
    }));
  };

  const renderAddonPicker = () => {
    if (!addons.length) {
      return null;
    }
    return (
      <div className={svcCss.addonBlock} data-testid="payers-addons">
        <p className={svcCss.addonHeading}>Add to this request</p>
        <div className={svcCss.addonChips} role="group" aria-label="Add-on options">
          {addons.map(addon => {
            const on = Boolean(enabledAddons[addon.id]);
            return (
              <button
                key={addon.id}
                type="button"
                className={on ? svcCss.addonChipActive : svcCss.addonChip}
                aria-pressed={on}
                onClick={() => toggleAddon(addon.id)}
                data-testid={`payers-addon-${addon.id}`}
              >
                {on ? '✓ ' : '+ '}
                {addon.label}
              </button>
            );
          })}
        </div>
        {addons
          .filter(a => enabledAddons[a.id])
          .map(addon => (
            <label key={`note-${addon.id}`} className={css.field}>
              <span className={css.fieldLabel}>{addon.label} notes</span>
              <textarea
                className={css.input}
                rows={2}
                placeholder={addon.placeholder}
                value={addonNotes[addon.id] || ''}
                onChange={e => setAddonNote(addon.id, e.target.value)}
              />
            </label>
          ))}
      </div>
    );
  };

  const renderField = field => {
    if (field.type === 'multiselect') {
      const opts = resolveFieldOptions(field);
      const selectedIds = multiselect[field.id] || [];
      return (
        <div key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <div className={svcCss.addonChips} role="group" aria-label={field.label}>
            {opts.map(opt => {
              const id = opt.id || opt.value;
              const on = selectedIds.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  className={on ? svcCss.addonChipActive : svcCss.addonChip}
                  aria-pressed={on}
                  onClick={() => toggleMulti(field.id, id)}
                  data-testid={`payers-multi-${field.id}-${id}`}
                >
                  {on ? '✓ ' : ''}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <label key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <textarea
            className={css.input}
            rows={3}
            placeholder={field.placeholder}
            value={formData[field.id] || ''}
            onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
          />
          {field.hint ? <span className={svcCss.fieldHint}>{field.hint}</span> : null}
        </label>
      );
    }

    if (field.type === 'select') {
      const opts = resolveFieldOptions(field);
      return (
        <label key={field.id} className={css.field}>
          <span className={css.fieldLabel}>
            {field.label}
            {field.required ? ' *' : ''}
          </span>
          <select
            className={css.input}
            value={formData[field.id] || ''}
            onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
          >
            <option value="">Choose…</option>
            {opts.map(o => (
              <option key={o.value || o.id} value={o.value || o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    return (
      <label key={field.id} className={css.field}>
        <span className={css.fieldLabel}>
          {field.label}
          {field.required ? ' *' : ''}
        </span>
        <input
          className={css.input}
          type={field.type || 'text'}
          placeholder={field.placeholder}
          min={field.min}
          step={field.step}
          value={formData[field.id] || ''}
          onChange={e => setFormData(prev => ({ ...prev, [field.id]: e.target.value }))}
        />
        {field.hint ? <span className={svcCss.fieldHint}>{field.hint}</span> : null}
        {field.id === 'weightKg' && showBigMoveNudge ? (
          <div className={svcCss.weightWarn} data-testid="courier-weight-warn">
            <p>
              Weight over {COURIER_WEIGHT_LIMIT_KG} kg is outside standard courier limits. Switch to{' '}
              <strong>Big Move</strong> for van/truck loads.
            </p>
            <button type="button" className={css.secondaryBtn} onClick={goToBigMove}>
              Continue as Big Move
            </button>
          </div>
        ) : null}
      </label>
    );
  };

  const renderAddonReview = () =>
    addons
      .filter(a => enabledAddons[a.id])
      .map(addon => (
        <div key={`rev-${addon.id}`} className={css.summaryRow}>
          <dt className={css.summaryKey}>{addon.label}</dt>
          <dd className={css.summaryVal}>{addonNotes[addon.id]?.trim() || 'Included'}</dd>
        </div>
      ));

  const renderFieldReview = () =>
    fields.map(f => {
      if (f.type === 'multiselect') {
        const values = multiselect[f.id] || [];
        if (!values.length) {
          return null;
        }
        return (
          <div key={f.id} className={css.summaryRow}>
            <dt className={css.summaryKey}>{fieldLabel(f.id)}</dt>
            <dd className={css.summaryVal}>
              {values.map(v => optionLabel(f, v)).join(', ')}
            </dd>
          </div>
        );
      }
      if (!formData[f.id]) {
        return null;
      }
      const display =
        f.type === 'select' ? optionLabel(f, formData[f.id]) : formData[f.id];
      return (
        <div key={f.id} className={css.summaryRow}>
          <dt className={css.summaryKey}>{fieldLabel(f.id)}</dt>
          <dd className={css.summaryVal}>{display}</dd>
        </div>
      );
    });

  return (
    <div
      className={css.root}
      style={{ '--app-accent': app.accent }}
      data-testid="simple-app-payers-delivery"
    >
      <header className={css.hero}>
        <h1 className={css.title}>{experience.headline}</h1>
        <p className={css.promise}>{experience.promise}</p>
      </header>

      <div className={`${svcCss.tabs} ${svcCss.tabsThree}`} role="tablist" aria-label="Payers & Delivery">
        {Object.values(TABS).map(id => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={tab === id ? svcCss.tabActive : svcCss.tab}
            onClick={() => switchTab(id)}
            data-testid={`payers-tab-${id}`}
          >
            {TAB_LABELS[id]}
          </button>
        ))}
      </div>

      {stage === STAGES.explore ? (
        <>
          <ColdStartModes
            appId={app.id}
            onRequest={startCreate}
            requestLabel={tabCopy.createCta || experience.createCta || 'Post a runner request'}
          />
          <section>
            <h2 className={css.sectionTitle}>{tabCopy.exploreTitle || experience.exploreTitle}</h2>
            <p className={css.demoBanner}>
              Operator seed examples · {DEMO_PROXY_RUNNERS.length} runners ready for match demos
            </p>
            <div className={css.cardList}>
              {demoItems.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={css.card}
                  onClick={() => {
                    setSelected(item);
                    setFormData(emptyForm());
                    setStage(STAGES.detail);
                  }}
                  data-testid={`payers-demo-${item.id}`}
                >
                  {item.image ? (
                    <img className={css.cardMedia} src={item.image} alt="" loading="lazy" />
                  ) : null}
                  <div className={css.cardBody}>
                    <div className={css.cardTop}>
                      <h3 className={css.cardTitle}>{item.title}</h3>
                      <span className={css.badge}>{item.badge}</span>
                    </div>
                    <p className={css.cardMeta}>{item.meta}</p>
                    <p className={css.cardBlurb}>{item.blurb}</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
          <div className={css.actionRow}>
            <button
              type="button"
              className={css.primaryBtn}
              onClick={startCreate}
              data-testid="payers-create"
            >
              {tabCopy.createCta || experience.createCta}
            </button>
          </div>
          {myJobs.length ? (
            <section style={{ marginTop: 18 }} data-testid="payers-my-requests">
              <h2 className={css.sectionTitle}>My runner requests</h2>
              <IdentitySyncBar syncStatus={syncStatus} compact />
              <ul className={css.requestsList}>
                {myJobs.slice(0, 6).map(j => (
                  <li key={j.id}>
                    <button
                      type="button"
                      className={css.requestItem}
                      onClick={() => {
                        setActiveJobId(j.id);
                        setStage(STAGES.job);
                      }}
                    >
                      <div>
                        <p className={css.requestTitle}>{j.title}</p>
                        <p className={css.requestMeta}>
                          {statusLabel(j.status)}
                          {j.providerName ? ` · ${j.providerName}` : ''}
                          {j.quotes?.length ? ` · ${j.quotes.length} quote(s)` : ''}
                        </p>
                      </div>
                      <span className={css.badge}>Open</span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className={css.secondaryLinkWrap}>
                <Link to="/activity" className={css.secondaryLink}>
                  All activity
                </Link>
              </p>
            </section>
          ) : null}
          <p className={css.secondaryLinkWrap}>
            <Link to="/landing/run" className={css.secondaryLink}>
              Become a runner
            </Link>
          </p>
          <div style={{ marginTop: 20 }}>
            <SupplyWaitlistForm kind="proxy" />
          </div>
          <CommunityAskBar appId={app.id} />
        </>
      ) : null}

      {stage === STAGES.detail && selected ? (
        <section className={css.panel}>
          <p className={css.demoBanner}>Demo listing</p>
          {selected.image ? (
            <img className={css.detailMedia} src={selected.image} alt="" />
          ) : null}
          <h2 className={css.sectionTitle}>{selected.title}</h2>
          <p className={css.cardMeta}>{selected.meta}</p>
          <p className={css.cardBlurb}>{selected.blurb}</p>
          {renderAddonPicker()}
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button type="button" className={css.secondaryBtn} onClick={resetExplore}>
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              onClick={() => setStage(STAGES.review)}
            >
              I’m interested
            </button>
          </div>
        </section>
      ) : null}

      {stage === STAGES.create ? (
        <section className={css.panel}>
          <h2 className={css.sectionTitle}>{tabCopy.createTitle || experience.createTitle}</h2>
          {progressive ? (
            <ProgressiveFormChrome
              steps={formSteps}
              stepIndex={formStepIndex}
              onePager={onePager}
              onToggleOnePager={() => {
                setOnePager(v => !v);
                setFormStepIndex(0);
              }}
              onSelectStep={setFormStepIndex}
            />
          ) : null}
          <div className={css.fields}>
            {visibleFields.map(renderField)}
            {showAddonsOnStep ? renderAddonPicker() : null}
          </div>
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button type="button" className={css.secondaryBtn} onClick={backFromCreate}>
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              disabled={
                isLastFormStep
                  ? !canSubmit || showBigMoveNudge
                  : !canAdvanceStep
              }
              onClick={advanceCreate}
              title={
                showBigMoveNudge && isLastFormStep
                  ? `Weight exceeds ${COURIER_WEIGHT_LIMIT_KG} kg — use Big Move`
                  : undefined
              }
            >
              {isLastFormStep ? 'Continue' : 'Next'}
            </button>
          </div>
          <CommunityAskBar appId={app.id} prompt="Talk with the community" />
        </section>
      ) : null}

      {stage === STAGES.review ? (
        <section className={css.panel}>
          <h2 className={css.sectionTitle}>Review & confirm</h2>
          <dl className={css.summaryList}>
            <div className={css.summaryRow}>
              <dt className={css.summaryKey}>Type</dt>
              <dd className={css.summaryVal}>{TAB_LABELS[tab]}</dd>
            </div>
            {selected ? (
              <>
                <div className={css.summaryRow}>
                  <dt className={css.summaryKey}>Listing</dt>
                  <dd className={css.summaryVal}>{selected.title}</dd>
                </div>
                <div className={css.summaryRow}>
                  <dt className={css.summaryKey}>Details</dt>
                  <dd className={css.summaryVal}>{selected.meta}</dd>
                </div>
              </>
            ) : (
              renderFieldReview()
            )}
            {renderAddonReview()}
          </dl>

          <label className={svcCss.termsBox} data-testid="payers-terms">
            <input
              type="checkbox"
              checked={Boolean(formData.termsAccepted)}
              onChange={e =>
                setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }))
              }
              data-testid="payers-terms-ack"
            />
            <span>{DELIVERY_TERMS_SUMMARY}</span>
          </label>

          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button
              type="button"
              className={css.secondaryBtn}
              onClick={() => setStage(selected ? STAGES.detail : STAGES.create)}
            >
              Back
            </button>
            <button
              type="button"
              className={css.primaryBtn}
              disabled={selected ? !formData.termsAccepted : !canConfirm}
              onClick={publishProxyJob}
            >
              Post & get runner quotes
            </button>
          </div>
        </section>
      ) : null}

      {stage === STAGES.job && activeJobId ? (
        <JobWorkspace
          jobId={activeJobId}
          onBackToList={resetExplore}
          onDone={() => setStage(STAGES.success)}
        />
      ) : null}

      {stage === STAGES.success ? (
        <section className={css.panel} data-testid="payers-success">
          <h2 className={css.sectionTitle}>
            {tabCopy.successTitle || experience.successTitle}
          </h2>
          <p className={css.cardBlurb}>{tabCopy.successBody || experience.successBody}</p>
          <div className={css.actionRow} style={{ marginTop: 16 }}>
            <button type="button" className={css.primaryBtn} onClick={resetExplore}>
              Back to listings
            </button>
          </div>
        </section>
      ) : null}

      <details className={css.techDetails}>
        <summary className={css.techSummary}>How it works behind the scenes</summary>
        <div className={css.techBody}>
          <ul>
            {(experience.technicalNotes || []).map(note => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      </details>
    </div>
  );
};

export default PayersDeliveryAppExperience;
